import type { StoreHit } from '@/lib/algolia';
import { BRAND } from '@/lib/cms/config';
import type { WatchProductSummary } from '@/lib/service/ecs/contracts';
import type {
  RepairDeliveryMethod,
  RepairReturnMethod,
  RepairShippingKitOption,
} from '@/lib/service/ecs/logisticsCapabilities';
import type { Watch } from '@/lib/myWatches/types';

import {
  REPAIR_REQUEST_INITIAL_STATE,
  type RepairRequestReason,
  type RepairRequestState,
  type RepairRequestStoredAddress,
} from './types';

export const REPAIR_REQUEST_STORAGE_KEY = `${BRAND}.repair_request.state`;
export const REPAIR_REQUEST_STORAGE_VERSION = 4;

interface StoredRepairRequestState {
  version: number;
  reasons: RepairRequestReason[];
  reasonComment: string;
  serialNumber: string | null;
  referenceNumber: string | null;
  watchWarranty: Watch | null;
  watchProductSummary: WatchProductSummary | null;
  deliveryMethod: RepairDeliveryMethod | null;
  returnMethod: RepairReturnMethod | null;
  shippingKitOption: RepairShippingKitOption | null;
  serviceBoutique: StoreHit | null;
  shippingAddress: RepairRequestStoredAddress | null;
  billingAddress: RepairRequestStoredAddress | null;
  differentBillingAddress: boolean;
  email: string;
  phone: string;
  phonePrefixCode: string;
}

function isReasonArray(value: unknown): value is RepairRequestReason[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as RepairRequestReason).label === 'string' &&
        typeof (item as RepairRequestReason).value === 'string'
    )
  );
}

function readStringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function readString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function readBoolean(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function readDeliveryMethod(value: unknown): RepairDeliveryMethod | null {
  return value === 'send' || value === 'boutique' ? value : null;
}

function readReturnMethod(value: unknown): RepairReturnMethod | null {
  return value === 'clientShipping' || value === 'boutiquePickup' ? value : null;
}

function readShippingKitOption(value: unknown): RepairShippingKitOption | null {
  return value === 'BreitlingSending' || value === 'ClientSending' ? value : null;
}

function readStoredAddress(value: unknown): RepairRequestStoredAddress | null {
  if (!value || typeof value !== 'object') return null;
  const address = value as RepairRequestStoredAddress;
  if (
    typeof address.firstName !== 'string' ||
    typeof address.lastName !== 'string' ||
    typeof address.streetAddress1 !== 'string' ||
    typeof address.city !== 'string' ||
    typeof address.postalCode !== 'string' ||
    typeof address.country !== 'string'
  ) {
    return null;
  }
  return address;
}

function readServiceBoutique(value: unknown): StoreHit | null {
  if (!value || typeof value !== 'object') return null;
  const boutique = value as StoreHit;
  return typeof boutique.store_id === 'string' ? boutique : null;
}

export function loadRepairRequestState(): RepairRequestState {
  if (globalThis.window === undefined) {
    return { ...REPAIR_REQUEST_INITIAL_STATE };
  }

  const raw = sessionStorage.getItem(REPAIR_REQUEST_STORAGE_KEY);
  if (!raw) {
    return { ...REPAIR_REQUEST_INITIAL_STATE };
  }

  try {
    const parsed = JSON.parse(raw) as Partial<StoredRepairRequestState>;
    if (parsed.version !== REPAIR_REQUEST_STORAGE_VERSION) {
      return { ...REPAIR_REQUEST_INITIAL_STATE };
    }

    return {
      reasons: isReasonArray(parsed.reasons)
        ? parsed.reasons
        : REPAIR_REQUEST_INITIAL_STATE.reasons,
      reasonComment:
        typeof parsed.reasonComment === 'string'
          ? parsed.reasonComment
          : REPAIR_REQUEST_INITIAL_STATE.reasonComment,
      serialNumber: readStringOrNull(parsed.serialNumber),
      referenceNumber: readStringOrNull(parsed.referenceNumber),
      watchWarranty:
        parsed.watchWarranty && typeof parsed.watchWarranty === 'object'
          ? (parsed.watchWarranty as Watch)
          : null,
      watchProductSummary:
        parsed.watchProductSummary && typeof parsed.watchProductSummary === 'object'
          ? (parsed.watchProductSummary as WatchProductSummary)
          : null,
      deliveryMethod: readDeliveryMethod(parsed.deliveryMethod),
      returnMethod: readReturnMethod(parsed.returnMethod),
      shippingKitOption: readShippingKitOption(parsed.shippingKitOption),
      serviceBoutique: readServiceBoutique(parsed.serviceBoutique),
      shippingAddress: readStoredAddress(parsed.shippingAddress),
      billingAddress: readStoredAddress(parsed.billingAddress),
      differentBillingAddress: readBoolean(parsed.differentBillingAddress),
      email: readString(parsed.email),
      phone: readString(parsed.phone),
      phonePrefixCode: readString(parsed.phonePrefixCode),
    };
  } catch {
    sessionStorage.removeItem(REPAIR_REQUEST_STORAGE_KEY);
    return { ...REPAIR_REQUEST_INITIAL_STATE };
  }
}

export function saveRepairRequestState(state: RepairRequestState): void {
  if (globalThis.window === undefined) return;

  const payload: StoredRepairRequestState = {
    version: REPAIR_REQUEST_STORAGE_VERSION,
    reasons: state.reasons,
    reasonComment: state.reasonComment,
    serialNumber: state.serialNumber,
    referenceNumber: state.referenceNumber,
    watchWarranty: state.watchWarranty,
    watchProductSummary: state.watchProductSummary,
    deliveryMethod: state.deliveryMethod,
    returnMethod: state.returnMethod,
    shippingKitOption: state.shippingKitOption,
    serviceBoutique: state.serviceBoutique,
    shippingAddress: state.shippingAddress,
    billingAddress: state.billingAddress,
    differentBillingAddress: state.differentBillingAddress,
    email: state.email,
    phone: state.phone,
    phonePrefixCode: state.phonePrefixCode,
  };

  sessionStorage.setItem(REPAIR_REQUEST_STORAGE_KEY, JSON.stringify(payload));
}

export function clearRepairRequestState(): void {
  if (globalThis.window === undefined) return;
  sessionStorage.removeItem(REPAIR_REQUEST_STORAGE_KEY);
}
